import { Component, createRef, useContext, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { confirm, custom } from 'devextreme/ui/dialog';
import Form, { GroupItem, SimpleItem, Label, EmptyItem, RequiredRule } from 'devextreme-react/form';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { Position } from 'devextreme-react/lookup';
import notify from 'devextreme/ui/notify';
import { DropDownBox } from 'devextreme-react/drop-down-box';
import { TextBox } from 'devextreme-react/text-box';
import { DataGrid, Column, Editing, Lookup, RequiredRule as GridRequiredRule } from 'devextreme-react/data-grid';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { SetAuthorizationHeaderAsync, CreateAuthorizedDataSource, callStatusData } from '~/helpers/data';
import { appConst } from '~/app-const';
import { AppContext } from '~/app-context';
import { CallLogLookupsContext } from '~/call-log-lookups-context';
import { NameIdentifier } from '~/helpers/miscellaneous';
import { NameAutoComplete, CategorySelectorContent } from '~/components';

const URL = `${window.env.apiEndpoint}/api/v1/CallLogs`;
const openCallIds = new Set();
const closeHandlers = new Set();
const refreshHandlers = new Set();

export function closeAllCallLogPopups() {
  [...closeHandlers].forEach(fn => fn());
}

export function subscribeCallLogSaved(cb) {
  refreshHandlers.add(cb);
  return () => refreshHandlers.delete(cb);
}

function personItemRender(item) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>
        {item.FullName}
        <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#888' }}>{item.Type}</span>
      </span>
      {item.BirthDate && (
        <span style={{ fontSize: '0.85em', color: '#888' }}>
          {dayjs(item.BirthDate).format('YYYY-MM-DD')}
        </span>
      )}
    </div>
  );
}

function PersonNameCell({ personId, dataSource }) {
  const [name, setName] = useState('');
  useEffect(() => {
    if (!personId) { setName(''); return; }
    dataSource.store().byKey(personId).then(p => setName(p?.FullName ?? '')).catch(() => {});
  }, [personId]);
  return <span>{name}</span>;
}

class CallLogPopupClass extends Component {
  static contextType = AppContext;
  constructor(props) {
    super(props);
    this.state = {
      categoryDisplayText: '',
      autocompleteKey: 0,
      initialized: false,
      saving: false,
    };
    this._autoSave = false;
    this._changed = false;
    this._newCallId = null;
    this._registered = false;
    this._autoSavePromise = null;

    this.minimizeButtonOptions = {
      icon: 'upload',
      onClick: () => {
        this.saveButtonOptions.onClick();
      },
    };

    this.currentCallLog = createStore({
      loadUrl: `${URL}/GetCallLog`,
      updateUrl: `${URL}/Put`,
      insertUrl: `${URL}/Post`,
      deleteUrl: `${URL}/Delete`,
      key: 'Id',
      onBeforeSend: async (method, ajaxOptions) => {
        const { Id } = this.props;
        await SetAuthorizationHeaderAsync(ajaxOptions);

        if (method === 'load') {
          ajaxOptions.data.key = Id;
          ajaxOptions.data.timeZone = window.env.timeZone;
        }
        if (method === 'update') {
          const dto = JSON.parse(ajaxOptions.data.values || '{}');
          const key = JSON.parse(ajaxOptions.data.key);
          ajaxOptions.url += `?key=${key}&timeZone=${encodeURIComponent(window.env.timeZone)}&autoSave=${this._autoSave}`;
          ajaxOptions.contentType = 'application/json';
          ajaxOptions.data = JSON.stringify(dto);
        }
        if (method === 'insert') {
          const dto = JSON.parse(ajaxOptions.data.values || '{}');
          ajaxOptions.url += `?timeZone=${encodeURIComponent(window.env.timeZone)}`;
          ajaxOptions.contentType = 'application/json';
          ajaxOptions.data = JSON.stringify(dto);
        }
      },
    });

    this.closeButtonOptions = {
      icon: 'close',
      onClick: () => {
        if (!this.props.gridEditing) {
          const myDialog = custom({
            showTitle: false,
            messageHtml: '<p>The system has auto-saved the information you have recorded so far about this call.</p><ul><li>If you\'re finished recording the call, save it.  You will see it on the Call Logs screen if you need to change it later.</li><li>If you\'re still working on the call, you can keep it open.  It will appear in the "Open Calls" menu</li><li>If you don\'t want to keep this record at all, you may delete it.</li></ul>',
            buttons: [
              { text: 'Save&Close', onClick: () => this.saveAndCloseButtonOptions.onClick() },
              { text: 'Keep It Open', onClick: () => this.saveButtonOptions.onClick() },
              { text: 'Delete', onClick: () => this.deleteCallLog() },
            ],
          });
          myDialog.show();
        } else {
          this.props.hide();
        }
      },
    };

    this.peopleData = CreateAuthorizedDataSource({ loadUrl: `${URL}/GetAllPeople` }, null, 'Id', null, null, ['FullName']);
    this.involvementsPeopleData = CreateAuthorizedDataSource({ loadUrl: `${URL}/GetAllPeople` }, null, 'Id', null, null, ['FullName']);
    this.statusData = callStatusData;

    this.discardButtonOptions = {
      text: 'Discard',
      onClick: async () => {
        const confirmResult = await confirm("<i>Delete current call log</i>", "Delete");
        if (confirmResult) this.deleteCallLog();
      },
    };

    this.saveButtonOptions = {
      text: 'Save',
      onClick: () => { this.persist() },
    };

    this.saveAndCloseButtonOptions = {
      text: 'Save & Close',
      onClick: () => this.persist(2),
    };

    this.formRef = createRef();
    this.involvementsGridRef = createRef();
    this.renderCategoryContent = this.renderCategoryContent.bind(this);
    this.autoSave = this.autoSave.bind(this);
    this.selectBoxChanged = this.selectBoxChanged.bind(this);
    this.loadCall = this.loadCall.bind(this);
    this.onPersonSelected = this.onPersonSelected.bind(this);
    this.onItemSelected = (item) => this.onPersonSelected(item.Id, item);
    this.onCleared = () => this.onPersonSelected(null, null);

    this.onDropdownSelectionChanged = this.onDropdownSelectionChanged.bind(this);
    this.renderCategoryField = this.renderCategoryField.bind(this);
  }

  buildCategoryDisplayText(categories) {
    if (!categories?.length) return '';
    const { lookups } = this.props;
    return categories.map(c => {
      const catId = c.CategoryId ?? c.categoryId;
      const subCatId = c.SubCategoryId ?? c.subCategoryId;
      const cat = (lookups?.categories ?? []).find(x => x.Value === catId);
      if (subCatId) {
        const sub = (lookups?.subcategories ?? []).find(x => x.Value === subCatId);
        return `${cat?.Text ?? ''}(${sub?.Text ?? ''})`;
      }
      return cat?.Text ?? '';
    }).join(', ');
  }

  onDropdownSelectionChanged(selection) {
    const text = (selection ?? [])
      .map(x => x.subCategoryId ? `${x.categoryText}(${x.subCategoryText})` : x.categoryText)
      .join(', ');

    this.setState(prev => {
      const existing = prev.currentCallLog?.Categories ?? [];
      const mapped = (selection ?? []).map(x => {
        const match = existing.find(e =>
          (e.CategoryId ?? e.categoryId) === x.categoryId &&
          (e.SubCategoryId ?? e.subCategoryId ?? null) === (x.subCategoryId ?? null)
        );
        return { Id: match?.Id ?? 0, CategoryId: x.categoryId, SubCategoryId: x.subCategoryId ?? null };
      });
      return {
        categoryDisplayText: text,
        currentCallLog: { ...prev.currentCallLog, Categories: mapped },
      };
    });
  }

  renderCategoryField(value) {
    return <TextBox value={value} readOnly />;
  }

  renderCategoryContent() {
    const { lookups } = this.props;
    const { currentCallLog } = this.state;

    return (
      <CategorySelectorContent
        categories={lookups?.categories ?? []}
        subcategories={lookups?.subcategories ?? []}
        onSelectionChanged={this.onDropdownSelectionChanged}
        selectedCategories={currentCallLog?.Categories ?? []}
      />
    );
  }


  getCurrentInvolvements() {
    try {
      const instance = this.involvementsGridRef?.current?.instance();
      const items = instance
        ? instance.getDataSource().items()
        : (this.state.currentCallLog?.InvolvedPeople ?? []);
      return items.map(({ Id, PersonId, InvolvementTypeId }) => ({ Id: Id > 0 ? Id : 0, PersonId, InvolvementTypeId }));
    } catch {
      return (this.state.currentCallLog?.InvolvedPeople ?? [])
        .map(({ Id, PersonId, InvolvementTypeId }) => ({ Id: Id > 0 ? Id : 0, PersonId, InvolvementTypeId }));
    }
  }

  async autoSave() {
    if (this.saving) return;
    const { currentCallLog } = this.state;
    if (!currentCallLog?.PersonId || !currentCallLog?.Details || !this._changed) return;

    const effectiveId = this.props.Id || this._newCallId;
    this.saving = true;
    this._autoSave = true;
    this._changed = false;
    this._autoSavePromise = (async () => {
      try {
        const autoSaveData = { ...currentCallLog, InvolvedPeople: this.getCurrentInvolvements() };
        if (effectiveId) {
          await this.currentCallLog.update(effectiveId, autoSaveData);
        } else {
          this._newCallId = await this.currentCallLog.insert(autoSaveData);
        }
      } catch {
        // silently ignore autosave errors
      } finally {
        this._autoSave = false;
        this.saving = false;
        this._autoSavePromise = null;
      }
    })();
    await this._autoSavePromise;
  }

  selectBoxChanged(e) {
    if (e.value !== e.previousValue && (typeof e.event !== 'undefined')) {
      this._changed = true;
    }
  }

  async persist(closeWithStatus) {
    if (this._persisting) return;
    if (!this.state.currentCallLog?.PersonId) {
      notify('Person is required.', 'error', 3000);
      return;
    }
    const validationResult = this.formRef.current.instance().validate();
    if (!validationResult.isValid) return;

    const gridInstance = this.involvementsGridRef?.current?.instance();
    if (gridInstance) {
      await gridInstance.saveEditData();
      if (gridInstance.hasEditData()) {
        notify('Please complete all involved people rows before saving.', 'error', 3000);
        return;
      }
    }

    if (this.getCurrentInvolvements().some(ip => !ip.PersonId || !ip.InvolvementTypeId)) {
      notify('All involved people must have a person and involvement type selected.', 'error', 3000);
      return;
    }

    this._persisting = true;
    this.setState({ saving: true });
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this._autoSavePromise) {
      await this._autoSavePromise.catch(() => {});
    }

    const { Id, hide, reloadCalls } = this.props;
    const effectiveId = Id || this._newCallId;
    const { currentCallLog } = this.state;
    const involvements = this.getCurrentInvolvements();
    const data = closeWithStatus != null
      ? { ...currentCallLog, Status: closeWithStatus, InvolvedPeople: involvements }
      : { ...currentCallLog, InvolvedPeople: involvements };
    this._autoSave = false;
    try {
      if (effectiveId) {
        await this.currentCallLog.update(effectiveId, data);
      } else {
        await this.currentCallLog.insert(data);
      }
      hide();
      await reloadCalls?.();
      refreshHandlers.forEach(fn => fn());
    } catch (error) {
      notify(error.message, 'error', 3000);
    } finally {
      this._persisting = false;
      this.setState({ saving: false });
    }
  }

  async deleteCallLog() {
    const { Id, hide, reloadCalls } = this.props;
    const effectiveId = Id || this._newCallId;
    if (!effectiveId) { hide(); return; }
    try {
      await this.currentCallLog.remove(effectiveId);
      hide();
      await reloadCalls?.();
      refreshHandlers.forEach(fn => fn());
    } catch (error) {
      notify(error.message, 'error', 3000);
    }
  }

  async loadCall() {
    try {
      const data = await this.currentCallLog.load();
      if (!data[0]) return;

      if (data[0].AutoSave) {
        const confirmResult = await confirm("<i>This call record is not saved yet. Retrieve from auto save?</i>", "Auto Save");
        if (confirmResult) {
          data[0].Details = data[0].AutoSaveDetails;
          data[0].UserId = data[0].AutoSaveUserId;
          data[0].CallDate = data[0].AutoSaveCallDate;
          data[0].CallLogActionId = data[0].AutoSaveCallLogActionId;
          data[0].Status = data[0].AutoSaveStatus;
        }
        try {
          await this.currentCallLog.update(this.props.Id, data[0]);
        } catch (error) {
          notify(error.message, 'error', 3000);
        }
      }

      this.setState((prev) => ({
        currentCallLog: data[0],
        categoryDisplayText: this.buildCategoryDisplayText(data[0].Categories),
        selectedPersonFullName: data[0].PersonId
          ? NameIdentifier(data[0].PreferredFirstName, data[0].FirstName, data[0].LastName)
          : undefined,
        autocompleteKey: prev.autocompleteKey + 1,
      }), () => {
        const saveLoop = async () => {
          await this.autoSave();
          this.timer = setTimeout(saveLoop, 5000);
        };
        saveLoop();
      });
    } catch (error) {
      notify(error.message, 'error', 3000);
    }
  }

  onPersonSelected(personId, item) {
    const { currentCallLog } = this.state;
    this.setState({
      currentCallLog: { ...currentCallLog, PersonId: personId },
      selectedPersonFullName: item?.FullName,
    });
  }

  async initNewCall() {
    this._newCallId = null;
    const { personId } = this.props;
    const userId = this.context?.userId ?? null;

    let personName;
    if (personId) {
      const person = await this.peopleData.store().byKey(personId).catch(() => null);
      personName = person ? NameIdentifier(person.PreferredFirstName, person.FirstName, person.LastName) : undefined;
    }

    this.setState((prev) => ({
      currentCallLog: {
        CallDate: dayjs().toDate(),
        UserId: userId,
        Status: this.statusData[0]?.Value,
        ...(personId ? { PersonId: personId } : {}),
      },
      selectedPersonFullName: personName,
      autocompleteKey: prev.autocompleteKey + 1,
    }), () => {
      const saveLoop = async () => {
        await this.autoSave();
        this.timer = setTimeout(saveLoop, 5000);
      };
      saveLoop();
    });
  }

  componentDidMount() {
    if (this.props.Id) {
      if (openCallIds.has(this.props.Id)) {
        notify('This call is already open.', 'warning', 2000);
        this.props.hide();
        return;
      }
      openCallIds.add(this.props.Id);
      this._registered = true;
      this.setState({ initialized: true });
      this.loadCall();
    } else {
      this.setState({ initialized: true });
      this.initNewCall();
    }
    this._closeHandler = () => this.props.hide();
    closeHandlers.add(this._closeHandler);
  }

  componentWillUnmount() {
    if (this.timer) clearTimeout(this.timer);
    if (this._registered && this.props.Id) {
      openCallIds.delete(this.props.Id);
      this._registered = false;
    }
    closeHandlers.delete(this._closeHandler);
  }

  componentDidUpdate(prevProps) {
    if (this.props.Id && this.props.Id !== prevProps.Id) {
      if (this._registered && prevProps.Id) {
        openCallIds.delete(prevProps.Id);
        this._registered = false;
      }
      if (openCallIds.has(this.props.Id)) {
        notify('This call is already open.', 'warning', 2000);
        this.props.hide();
        return;
      }
      openCallIds.add(this.props.Id);
      this._registered = true;
      this.loadCall();
    } else if (!this.props.Id && prevProps.Id) {
      if (this._registered) {
        openCallIds.delete(prevProps.Id);
        this._registered = false;
      }
      if (this.timer) clearTimeout(this.timer);
      this.initNewCall();
    }
  }
  render() {
    const { offset, refreshCallLogDropDownButton, Id, gridEditing, lookups } = this.props;
    const { currentCallLog, categoryDisplayText, selectedPersonFullName, autocompleteKey, initialized, saving } = this.state;
    if (!initialized) return null;
    return (
      <Popup
        visible={this.props.visible}
        width="40%"
        dragEnabled
        hideOnOutsideClick={false}
        showTitle
        shading={false}
        showCloseButton={false}
        animation={null}
        onShowing={refreshCallLogDropDownButton}
      >
        <Position offset={offset} />
        <ToolbarItem
          text={selectedPersonFullName ?? (!Id ? 'New Call' : 'Update Call')}
          location="before"
        />
        <ToolbarItem
          visible={!gridEditing}
          widget="dxButton"
          location="after"
          options={{ ...this.minimizeButtonOptions, disabled: saving }}
        />
        <ToolbarItem
          widget="dxButton"
          location="after"
          options={this.closeButtonOptions}
        />
        <TabPanel animationEnabled={false}>
          <TabItem title="Properties">
        <Form
          ref={this.formRef}
          scrollingEnabled
          formData={currentCallLog}
          {...appConst.defaultFormOptions}
        >
          <GroupItem showCaption={false} colCount={2}>
            <SimpleItem colSpan={2}>
              <Label text="Person" />
              <NameAutoComplete
                key={autocompleteKey}
                dataSource={this.peopleData}
                onItemSelected={this.onItemSelected}
                onCleared={this.onCleared}
                itemRender={personItemRender}
                defaultValue={selectedPersonFullName}
                searchExpr={['PreferredFirstName', 'FirstName', 'LastName', 'FullName', 'PreviousLastName', 'PreviousFirstName']}
              />
            </SimpleItem>
            <SimpleItem
              editorType="dxSelectBox"
              dataField="Status"
              editorOptions={{
                dataSource: this.statusData,
                ...appConst.defaultSelectBoxOptions,
                valueExpr: 'Value',
                displayExpr: 'Text',
                grouped: false,
                showClearButton: false,
                searchEnabled: false,
              }}
            >
              <RequiredRule message="Status is required" />
            </SimpleItem>
            <SimpleItem
              editorType="dxSelectBox"
              dataField="UserId"
              editorOptions={{
                dataSource: lookups?.usersData ?? [],
                ...appConst.defaultSelectBoxOptions,
                valueExpr: 'Id',
                displayExpr: 'Name',
                onValueChanged: this.selectBoxChanged,
                grouped: false,
              }}
            >
              <Label text="User" />
              <RequiredRule message="User is required" />
            </SimpleItem>
            <SimpleItem
              editorType="dxDateBox"
              dataField="CallDate"
              editorOptions={{
                type: 'datetime',
                displayFormat: 'yyyy-MM-dd hh:mm a',
                showClearButton: true,
                invalidDateMessage: 'Value must be a date and time.',
                onValueChanged: this.selectBoxChanged,
              }}
            >
              <Label text="Call Date" />
              <RequiredRule message="Call Date is required" />
            </SimpleItem>
            <SimpleItem
              editorType="dxSelectBox"
              dataField="CallLogActionId"
              editorOptions={{
                dataSource: lookups?.actionsData ?? [],
                ...appConst.defaultSelectBoxOptions,
                customItemCreateEvent: 'input',
                onValueChanged: this.selectBoxChanged,
              }}
            >
              <Label text="Action" />
            </SimpleItem>
            <SimpleItem colSpan={2}>
              <Label text="Categories" />
              <DropDownBox
                value={categoryDisplayText}
                fieldRender={this.renderCategoryField}
                contentRender={this.renderCategoryContent}
                placeholder="Select categories..."
                hint={categoryDisplayText}
              />
            </SimpleItem>
            <SimpleItem
              editorType="dxTextArea"
              dataField="Details"
              colSpan={2}
              editorOptions={{
                height: '8em',
                onValueChanged: this.selectBoxChanged,
                valueChangeEvent: 'keyup',
              }}
            >
              <Label text="Details" />
            </SimpleItem>
          </GroupItem>
        </Form>
          </TabItem>
          <TabItem title="Involved People">
        <DataGrid
          ref={this.involvementsGridRef}
          dataSource={currentCallLog?.InvolvedPeople ?? []}
          keyExpr="Id"
          showBorders
          onInitNewRow={(e) => { e.data.Id = -Date.now(); }}
          onToolbarPreparing={(e) => {
            const addButton = e.toolbarOptions.items.find(item => item.name === 'addRowButton');
            if (addButton) {
              addButton.options.onClick = async () => {
                const instance = this.involvementsGridRef?.current?.instance();
                if (instance) {
                  await instance.saveEditData();
                  if (!instance.hasEditData()) instance.addRow();
                }
              };
            }
          }}
        >
          <Editing mode="row" allowAdding allowDeleting allowUpdating />
          <Column
            dataField="PersonId"
            caption="Involved Person"
            alignment="left"
            width="66%"
            cellRender={(cellInfo) => (
              <PersonNameCell personId={cellInfo.value} dataSource={this.involvementsPeopleData} />
            )}
            editCellRender={(cellInfo) => (
              <NameAutoComplete
                dataSource={this.involvementsPeopleData}
                onItemSelected={(item) => cellInfo.setValue(item.Id)}
                onCleared={() => cellInfo.setValue(null)}
                itemRender={personItemRender}
                defaultValue={cellInfo.data.PersonFullName}
                searchExpr={['PreferredFirstName', 'FirstName', 'LastName', 'FullName', 'PreviousLastName', 'PreviousFirstName']}
              />
            )}
          >
            <GridRequiredRule />
          </Column>
          <Column dataField="InvolvementTypeId" caption="Involvement Type" width="34%">
            <Lookup dataSource={lookups?.involvementTypes ?? []} valueExpr="Value" displayExpr="Text" />
            <GridRequiredRule />
          </Column>
        </DataGrid>
          </TabItem>
        </TabPanel>
        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={{ ...this.saveButtonOptions, disabled: saving }}
        />
        <ToolbarItem
          visible={!gridEditing}
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={{ ...this.saveAndCloseButtonOptions, disabled: saving }}
        />
        <ToolbarItem
          visible={!gridEditing}
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={{ ...this.discardButtonOptions, disabled: saving }}
        />
      </Popup>
    );
  }
}
CallLogPopupClass.propTypes = {
  Id: PropTypes.number,
  personId: PropTypes.number,
  offset: PropTypes.string.isRequired,
  hide: PropTypes.func.isRequired,
  reloadCalls: PropTypes.func,
  gridEditing: PropTypes.bool,
  refreshCallLogDropDownButton: PropTypes.func.isRequired,
  lookups: PropTypes.object,
};

export function CallLogPopup(props) {
  const lookups = useContext(CallLogLookupsContext);
  return <CallLogPopupClass {...props} lookups={lookups} />;
}

